import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { asyncConnect } from 'react-async-client';
import { propEq, map, objOf, prop, filter, compose, not, path } from 'ramda';

import { eventCoexecutiveListTypes, VER_REK_XMLTYPE } from 'constants/events';
import { fullAutoOptions } from 'constants/asyncClient';
import { getOrganizationEmployee, getEventDictionaries } from 'actions/asyncActions';
import Select from 'components/common/form/Select';
import Text from 'components/common/form/Text';
import withAutoFocusForm from 'components/hocs/withAutoFocusForm';
import { requiredIf, maxLength } from 'utils/validators';
import EventDateTimeFields from './EventDateTimeFields';
import { contactIsRequired, companyIsRequired } from 'helpers/eventHelper';
import ListenerField from 'components/common/form/ListenerField';
import { getEventConfig } from 'helpers/eventHelper';

const companyIsRequiredValidator = requiredIf(companyIsRequired);
const contactIsRequiredValidator = requiredIf(contactIsRequired);

@withAutoFocusForm
@asyncConnect({
    getEventDictionaries: getEventDictionaries
        .withPayload(({ project, eventId }) => project ? project.id : eventId)
        .withOptions(fullAutoOptions),
    getOrganizationEmployee: getOrganizationEmployee
        .withPayload(({ organization: organizationId }) => ({ organizationId }))
        .withParams(({ organization: organizationId }) => ({ organizationId }))
        .withOptions(({ organization }) => ({
            resetOnUpdate: true,
            dispatchOnUpdate: true,
            resetOnUnmount: true,
            dispatchOnMount: !!organization
        })),
})
export default class EventFields extends Component {
    static propTypes = {
        person: PropTypes.object,
        project: PropTypes.object,
        participators: PropTypes.array.isRequired,
        getEventDictionaries: PropTypes.object.isRequired,
        getOrganizationEmployee: PropTypes.object.isRequired,
        getAvailableEventTypes: PropTypes.object,
        isEdit: PropTypes.bool,
        eventConfig: PropTypes.object.isRequired,
        change: PropTypes.func.isRequired,
        eventDateProps: PropTypes.object,
        eventTypes: PropTypes.array,
        type: PropTypes.number,
        typeName: PropTypes.string,
        organization: PropTypes.string,
        xmlType: PropTypes.string,
    };

    static defaultProps = {
        eventDateProps: {},
        eventConfig: {}
    }

    onCompanyChange = () => {
        this.props.change('contact.contact', 0);
    }

    getFilteredParticipantList(contactEmployee) {
        const { participantList = [] } = this.props.getEventDictionaries.data;
        const participantFilter = compose(not, propEq('id', Number(contactEmployee)));

        return filter(participantFilter, participantList);
    }

    getCoexecutiveList(eventConfig) {
        const dicts = this.props.getEventDictionaries.data;
        const dictName = eventCoexecutiveListTypes[eventConfig.soispdict];

        return dicts[dictName] || dicts.coexecutiveList;
    }

    validateOrganization = (value, values) => {
        const { getAvailableEventTypes, eventConfig: propsEventConfig  } = this.props;

        const eventConfig = getAvailableEventTypes ? (
            getEventConfig(values.contact.type, getAvailableEventTypes.data).view
        ) : propsEventConfig;

        return eventConfig && (eventConfig.company || eventConfig.companyAndEmployee)
            && companyIsRequiredValidator(value, values);
    }

    validateContact = (value, values) => {
        return contactIsRequiredValidator(value, values);
    }

    isOrganizationRequired = () => this.props.xmlType === VER_REK_XMLTYPE;

    render() {
        const employees = this.props.getOrganizationEmployee.data;
        const { companyList, responsibleList } = this.props.getEventDictionaries.data;
        const { organization, change, isEdit, eventDateProps, eventConfig, eventTypes, type } = this.props;
        const { getEventDictionaries, getOrganizationEmployee, getAvailableEventTypes } = this.props;

        const coexecutiveList = eventConfig && this.getCoexecutiveList(eventConfig);
        const isEmployeeContact = (eventConfig.employee || eventConfig.companyAndEmployee);

        return (
            <div>
                <Field
                    name='contact.type'
                    component={Select}
                    options={eventTypes}
                    dictionaryAction={getAvailableEventTypes}
                    valuePath='workCode'
                    hidden={isEdit}
                    disabled={!!type}
                    label={{
                        content: 'events.create.type',
                        required: true
                    }}
                />

                <ListenerField
                    listenFieldName={['details.time.from', 'details.time.to']}
                >
                    { ({ details: { time }}) => {
                        return (
                            <EventDateTimeFields
                                timeFrom={time.from}
                                timeTo={time.to}
                                change={change}
                                {...eventDateProps}
                            />
                        )
                    }}
                </ListenerField>
                <Field
                    name='contact.employee'
                    component={Select}
                    dictionaryAction={getEventDictionaries}
                    options={responsibleList}
                    hidden={isEdit}
                    label={{
                        content: 'events.create.responsible',
                        required: true
                    }}
                />
                <Field
                    name='contact.organization'
                    component={Select}
                    dictionaryAction={getEventDictionaries}
                    options={companyList}
                    hidden={!eventConfig.company && !eventConfig.companyAndEmployee}
                    validate={this.validateOrganization}
                    label={{
                        content: 'events.create.company',
                        required: !this.isOrganizationRequired(),
                        text: eventConfig.orgstr
                    }}
                    onChange={this.onCompanyChange}
                />
                <ListenerField
                    listenFieldName={[
                        'contact.type',
                    ]}
                >
                    {({ contact: { type: contactType } }) => (
                        <Field
                            name='contact.contact'
                            component={Select}
                            options={isEmployeeContact ? employees : coexecutiveList}
                            dictionaryAction={isEmployeeContact ? getOrganizationEmployee : getEventDictionaries}
                            disabled={isEmployeeContact && !organization}
                            validate={this.validateContact}
                            hidden={!eventConfig.employee && !eventConfig.companyAndEmployee && !eventConfig['co-executive']}
                            label={{
                                content: isEmployeeContact ? 'events.create.employee' : 'events.create.coexecutor',
                                required: contactIsRequired({ contactType }, path(['contactType'])),
                                text: eventConfig.soispstr
                            }}
                        />
                    )}
                </ListenerField>
                <ListenerField
                    listenFieldName={[
                        'contact.employee',
                    ]}
                >
                    { ({ contact }) => (
                        <Field
                            name='additionalPeople'
                            component={Select}
                            multi={true}
                            allowClear={true}
                            options={eventConfig && this.getFilteredParticipantList(contact.employee)}
                            dictionaryAction={getEventDictionaries}
                            stringifyValue={false}
                            hidden={!eventConfig.participant}
                            valueTransformer={map(prop('peopleId'))}
                            onChangeTransformer={map(objOf('peopleId'))}
                            label={{ content: 'events.create.participants' }}
                        />
                    )}
                </ListenerField>
                <Field
                    name='contact.description'
                    component={Text}
                    placeholder='placeholders.comment'
                    label={{ content: 'events.comment' }}
                    validate={maxLength(254)}
                />
            </div>
        );
    }
}
